/** Spec §4.4 trade list for Contractor Growth Kit onboarding. */
export const CGK_TRADES = [
  'Plumbing',
  'HVAC',
  'Electrical',
  'Roofing',
  'Landscaping/Lawn',
  'Painting',
  'General Contractor/Remodeling',
  'Flooring',
  'Concrete/Masonry',
  'Fencing',
  'Cleaning',
  'Pest Control',
  'Garage Door',
  'Handyman',
  'Other',
] as const;

export type CgkTrade = (typeof CGK_TRADES)[number];

/** Improvement priorities — personalize Home only; never lock features. */
export const CGK_PRIORITIES = [
  { id: 'search', label: 'Website visibility in search' },
  { id: 'content', label: 'Website content and service pages' },
  { id: 'quotes', label: 'Making it easier to request a quote' },
  { id: 'proof', label: 'Reviews and project photos' },
  { id: 'social', label: 'Social-media presence' },
] as const;

export type CgkPriorityId = (typeof CGK_PRIORITIES)[number]['id'];

export const CGK_CUSTOMER_TYPES = ['residential', 'commercial', 'both'] as const;
export type CgkCustomerType = (typeof CGK_CUSTOMER_TYPES)[number];

/** US states + DC for type-ahead. */
export const US_STATES: Array<{ code: string; name: string }> = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

/** Starter service chips by trade (owners can add custom). */
export const CGK_TRADE_SERVICES: Record<string, string[]> = {
  Plumbing: ['Drain cleaning', 'Water heater', 'Leak repair', 'Fixture install', 'Sewer line', 'Emergency plumbing'],
  HVAC: ['AC tune-up', 'Furnace repair', 'Heat pump', 'Duct cleaning', 'Thermostat', 'Emergency HVAC'],
  Electrical: ['Panel upgrade', 'Outlet/switch', 'Lighting', 'EV charger', 'Generator', 'Emergency electrical'],
  Roofing: ['Roof repair', 'Roof replacement', 'Inspection', 'Gutter work', 'Storm damage'],
  'Landscaping/Lawn': ['Lawn care', 'Mulching', 'Tree trimming', 'Hardscape', 'Seasonal cleanup'],
  Painting: ['Interior paint', 'Exterior paint', 'Cabinet refinishing', 'Deck stain'],
  'General Contractor/Remodeling': ['Kitchen remodel', 'Bath remodel', 'Additions', 'Basement finish'],
  Flooring: ['Hardwood', 'Tile', 'LVP/vinyl', 'Carpet', 'Refinishing'],
  'Concrete/Masonry': ['Driveway', 'Patio', 'Foundation', 'Retaining wall'],
  Fencing: ['Wood fence', 'Vinyl fence', 'Chain link', 'Gate repair'],
  Cleaning: ['Residential clean', 'Move-out clean', 'Carpet clean', 'Pressure wash'],
  'Pest Control': ['General pest', 'Termite', 'Rodent', 'Mosquito'],
  'Garage Door': ['Spring repair', 'Opener', 'Door install', 'Sensor/alignment'],
  Handyman: ['Odd jobs', 'Drywall', 'Caulking', 'Furniture assembly'],
  Other: ['Consultation', 'Repair', 'Install', 'Maintenance'],
};

export const PASSWORD_MIN_LENGTH = 10;
export const VERIFY_CODE_TTL_SECONDS = 30 * 60;
export const RESET_TOKEN_TTL_SECONDS = 60 * 60;
export const VERIFY_RESEND_COOLDOWN_SECONDS = 60;
export const VERIFY_RESEND_MAX_PER_HOUR = 5;
export const LOGIN_LOCKOUT_FAILURES = 5;
export const LOGIN_LOCKOUT_SECONDS = 15 * 60;
export const RECENT_LOGIN_SECONDS = 10 * 60;
export const REFERRAL_COOKIE_DAYS = 30;

export type OnboardingDraft = {
  status: 'not_started' | 'in_progress' | 'complete';
  step: number;
  priorities: CgkPriorityId[];
  noWebsite: boolean;
  customerType: CgkCustomerType | null;
  firstName: string;
  tradeOther: string;
  serviceAreaText: string;
};

export function defaultOnboardingDraft(): OnboardingDraft {
  return {
    status: 'not_started',
    step: 1,
    priorities: [],
    noWebsite: false,
    customerType: null,
    firstName: '',
    tradeOther: '',
    serviceAreaText: '',
  };
}

export function parseOnboardingDraft(raw: string | null | undefined): OnboardingDraft {
  const base = defaultOnboardingDraft();
  if (!raw || raw === 'not_started') return base;
  if (raw === 'complete') return { ...base, status: 'complete', step: 6 };
  try {
    const parsed = JSON.parse(raw) as Partial<OnboardingDraft>;
    return {
      ...base,
      ...parsed,
      priorities: Array.isArray(parsed.priorities) ? (parsed.priorities as CgkPriorityId[]) : [],
      step: typeof parsed.step === 'number' ? Math.min(6, Math.max(1, parsed.step)) : 1,
      status: parsed.status === 'complete' || parsed.status === 'in_progress' ? parsed.status : 'in_progress',
    };
  } catch {
    return base;
  }
}

export function serializeOnboardingDraft(draft: OnboardingDraft): string {
  return JSON.stringify(draft);
}

/** Rough password strength for UI hint only (not server gate beyond min length). */
export function passwordStrength(password: string): {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
} {
  let score = 0;
  if (password.length >= PASSWORD_MIN_LENGTH) score++;
  if (password.length >= 14) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  const clamped = Math.min(4, score) as 0 | 1 | 2 | 3 | 4;
  const labels = ['Too short', 'Weak', 'Fair', 'Good', 'Strong'] as const;
  return { score: clamped, label: labels[clamped] };
}
